import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { getProviderForModel, SDialog } from '@modules/ui-components';
import { AvailableModelSelector, useAvailableAIModels } from '@modules/ai-settings';
import type { ContentTopic } from '../../types';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { useThemeCatalog } from '../../hooks/useThemeCatalog';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import { ResearchBriefInputForm } from './ResearchBriefInputForm';
import { ResearchBriefEditor } from './ResearchBriefEditor';
import { ResearchAnalysisCard } from './ResearchAnalysisCard';
import { CellTaskBadge } from '../topic/CellTaskBadge';

type ResearchSearchProvider = 'auto' | 'perplexity' | 'gemini' | 'openai';
type ResearchDepth = 'standard' | 'deep';
type ResearchReasoningEffort = 'low' | 'medium' | 'high';
type FocusKind = 'topic' | 'aspect' | 'custom';
type PhaseKey = 'brief' | 'research' | 'analysis';

const SEARCH_PROVIDER_OPTIONS: Array<{ value: ResearchSearchProvider; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'perplexity', label: 'Perplexity' },
  { value: 'gemini', label: 'Gemini' },
  { value: 'openai', label: 'OpenAI' },
];

const RESEARCH_DEPTH_OPTIONS: Array<{ value: ResearchDepth; label: string }> = [
  { value: 'deep', label: 'Deep' },
  { value: 'standard', label: 'Standard' },
];

const RESEARCH_EFFORT_OPTIONS: Array<{ value: ResearchReasoningEffort; label: string }> = [
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
];

function defaultResearchModel(provider: Exclude<ResearchSearchProvider, 'auto'>, depth: ResearchDepth): string {
  if (provider === 'perplexity') return depth === 'deep' ? 'sonar-deep-research' : 'sonar-pro';
  if (provider === 'openai') return depth === 'deep' ? 'gpt-5.6-sol' : 'gpt-5.6-terra';
  return depth === 'deep' ? 'gemini-3.1-pro-preview' : 'gemini-3-flash-preview';
}

function searchProviderToAIProvider(provider: Exclude<ResearchSearchProvider, 'auto'>): 'google' | 'openai' | 'perplexity' {
  return provider === 'gemini' ? 'google' : provider;
}

function shouldResetResearchModel(
  model: string,
  searchProvider: Exclude<ResearchSearchProvider, 'auto'>,
  researchDepth: ResearchDepth,
): boolean {
  if (!model) return true;
  const expectedProvider = searchProviderToAIProvider(searchProvider);
  if (getProviderForModel(model) !== expectedProvider) return true;
  if (researchDepth === 'standard') return model.includes('deep-research');
  if (searchProvider === 'gemini') return model !== 'gemini-3.1-pro-preview';
  if (searchProvider === 'openai') return !model.includes('deep-research');
  if (searchProvider === 'perplexity') {
    return model !== 'sonar-pro' && model !== 'sonar-deep-research';
  }
  return false;
}

function nextIncompletePhase(run: any | null): PhaseKey {
  if (!run?.brief) return 'brief';
  if (!run?.research) return 'research';
  if (!run?.analysis) return 'analysis';
  return 'brief';
}

function phaseStatus(run: any, phase: PhaseKey): string {
  return run?.phases?.[phase]?.status ?? 'pending';
}

function formatFocusTitle(topic: ContentTopic, focusKind: FocusKind, aspectKey: string, customGoal: string): string {
  if (focusKind === 'aspect') {
    const aspect = ((topic as any).postBreakdown ?? []).find((a: any) => a.aspectKey === aspectKey);
    return aspect?.aspectLabel ? `Research: ${aspect.aspectLabel}` : 'Aspect research';
  }
  if (focusKind === 'custom') return customGoal ? `Research: ${customGoal.slice(0, 80)}` : 'Custom research';
  return topic.angle ? `Research: ${topic.angle.slice(0, 80)}` : 'Topic research';
}

export interface ResearchStudyDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  authorId: string;
  topic: ContentTopic;
  run: any | null;
  onRunCreated: (runId: string) => void;
}

export const ResearchStudyDialog: React.FC<ResearchStudyDialogProps> = ({
  open,
  onClose,
  projectId,
  userId,
  authorId,
  topic,
  run,
  onRunCreated,
}) => {
  const client = useMemo(() => new ContentPlanApiClient(), []);
  const {
    providers: availableProviderMap,
    projectProviders,
    loading: availableModelsLoading,
  } = useAvailableAIModels(projectId);
  const { themes: themeCatalog } = useThemeCatalog(projectId);
  const themeNamesById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const theme of themeCatalog ?? []) map[theme.id] = theme.name ?? theme.id;
    return map;
  }, [themeCatalog]);

  const aspects = (((topic as any).postBreakdown ?? []) as Array<{
    aspectKey: string;
    aspectLabel: string;
    aspectAngle?: string;
  }>).filter((a) => a.aspectKey);

  const [projectPreview, setProjectPreview] = useState<
    | { name?: string; domain?: string; audienceSummary?: string; targetCountries?: string[] }
    | undefined
  >(undefined);
  const [busy, setBusy] = useState<null | 'brief' | 'research' | 'analysis' | 'run_all'>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<PhaseKey | false>('brief');
  const [fullTextOpen, setFullTextOpen] = useState(false);
  const [focusKind, setFocusKind] = useState<FocusKind>('topic');
  const [aspectKey, setAspectKey] = useState<string>(aspects[0]?.aspectKey ?? '');
  const [customGoal, setCustomGoal] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [modelBrief, setModelBrief] = useState<string>(projectDefaultModel.value);
  const [searchProvider, setSearchProvider] = useState<ResearchSearchProvider>('auto');
  const [researchDepth, setResearchDepth] = useState<ResearchDepth>('deep');
  const [reasoningEffort, setReasoningEffort] = useState<ResearchReasoningEffort>('medium');
  const [modelResearch, setModelResearch] = useState<string>(projectDefaultModel.value);
  const [modelAnalysis, setModelAnalysis] = useState<string>(projectDefaultModel.value);
  const [modelsExpanded, setModelsExpanded] = useState(false);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(null);
    setModelBrief(projectDefaultModel.value);
    setModelResearch(projectDefaultModel.value);
    setModelAnalysis(projectDefaultModel.value);
    setExpanded(nextIncompletePhase(run));
    if (!run) {
      setFocusKind('topic');
      setAspectKey(aspects[0]?.aspectKey ?? '');
      setCustomGoal('');
      setTitleDraft('');
    }
  }, [open, run?.id, projectDefaultModel.value]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { CoreFirebaseService } = await import(
          '@modules/firebase-core/services/CoreFirebaseService'
        );
        const db = CoreFirebaseService.getInstance().firestore;
        const snap = await getDoc(doc(db, 'projects', projectId));
        if (!cancelled) setProjectPreview((snap.data() as any) ?? undefined);
      } catch {
        if (!cancelled) setProjectPreview(undefined);
      }
    })();
    return () => { cancelled = true; };
  }, [open, projectId]);

  React.useEffect(() => {
    if (searchProvider === 'auto') return;
    const nextDefault = defaultResearchModel(searchProvider, researchDepth);
    setModelResearch((prev) => {
      return shouldResetResearchModel(prev, searchProvider, researchDepth) ? nextDefault : prev;
    });
  }, [searchProvider, researchDepth]);

  const availableSearchProviderOptions = useMemo(() => {
    if (availableModelsLoading) return SEARCH_PROVIDER_OPTIONS;
    const hasProjectConfiguredProvider = SEARCH_PROVIDER_OPTIONS.some((option) => (
      option.value !== 'auto' && projectProviders[searchProviderToAIProvider(option.value)]?.configured
    ));
    return SEARCH_PROVIDER_OPTIONS.filter((option) => {
      if (option.value === 'auto') return true;
      const provider = searchProviderToAIProvider(option.value);
      if (hasProjectConfiguredProvider) return Boolean(projectProviders[provider]?.configured);
      return Boolean(availableProviderMap[provider]?.configured && availableProviderMap[provider]?.textModels);
    });
  }, [availableModelsLoading, availableProviderMap, projectProviders]);

  React.useEffect(() => {
    if (availableModelsLoading || searchProvider === 'auto') return;
    if (availableSearchProviderOptions.some((option) => option.value === searchProvider)) return;
    setSearchProvider('auto');
  }, [availableModelsLoading, availableSearchProviderOptions, searchProvider]);

  const explicitResearchModel =
    searchProvider === 'auto'
      ? null
      : modelResearch || defaultResearchModel(searchProvider, researchDepth);

  const buildFocus = () => {
    if (run?.focus) return run.focus;
    if (focusKind === 'aspect') {
      const aspect = aspects.find((a) => a.aspectKey === aspectKey);
      return {
        kind: 'aspect' as const,
        label: aspect?.aspectLabel ?? aspectKey,
        aspectKey,
        aspectAngle: aspect?.aspectAngle,
        goal: customGoal.trim() || undefined,
      };
    }
    if (focusKind === 'custom') {
      return {
        kind: 'custom' as const,
        label: customGoal.trim() || 'Custom research',
        goal: customGoal.trim() || undefined,
      };
    }
    return { kind: 'topic' as const, label: 'Topic-wide research' };
  };

  const studyTitle = titleDraft.trim()
    || run?.title
    || formatFocusTitle(topic, focusKind, aspectKey, customGoal);

  const composeBrief = async (
    payload: { userHints?: string; selectedPaths: string[]; hydratedData: unknown },
    reuseRunId?: string,
  ) => {
    setError(null);
    setBusy('brief');
    try {
      const resp = await client.composeTopicResearchBrief({
        projectId,
        userId,
        authorId,
        topicId: topic.id,
        userHints: payload.userHints,
        manualSelectionPaths: payload.selectedPaths,
        hydratedData: payload.hydratedData,
        runId: reuseRunId,
        title: studyTitle,
        focus: buildFocus(),
        models: { brief: modelBrief, research: explicitResearchModel, analysis: modelAnalysis },
        searchProvider,
        researchDepth,
        reasoningEffort,
      } as any);
      if (!resp.success || !resp.runId) throw new Error(resp.error ?? 'brief start failed');
      onRunCreated(resp.runId);
      setExpanded('brief');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const runAll = async () => {
    setError(null);
    setBusy('run_all');
    try {
      const resp = await client.runAllTopicResearch({
        projectId,
        userId,
        authorId,
        topicId: topic.id,
        title: studyTitle,
        focus: buildFocus(),
        models: { brief: modelBrief, research: explicitResearchModel, analysis: modelAnalysis },
        searchProvider,
        researchDepth,
        reasoningEffort,
      } as any);
      if (!resp.success || !resp.runId) throw new Error(resp.error ?? 'run-all start failed');
      onRunCreated(resp.runId);
      setExpanded('brief');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const runDeepResearch = async () => {
    if (!run?.id) return;
    setError(null);
    setBusy('research');
    try {
      const resp = await client.executeResearch({
        projectId,
        userId,
        authorId,
        runId: run.id,
        model: explicitResearchModel,
        searchProvider,
        researchDepth,
        reasoningEffort,
      });
      if (!resp.success) throw new Error(resp.error ?? 'deep research start failed');
      setExpanded('research');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const runAnalysis = async () => {
    if (!run?.id) return;
    setError(null);
    setBusy('analysis');
    try {
      const resp = await client.analyseResearch({
        projectId,
        userId,
        authorId,
        runId: run.id,
        model: modelAnalysis,
      });
      if (!resp.success) throw new Error(resp.error ?? 'analysis start failed');
      setExpanded('analysis');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const saveBrief = async (patch: Record<string, unknown>) => {
    if (!run?.id) return;
    setError(null);
    try {
      const resp = await client.patchResearchBrief({ projectId, authorId, runId: run.id, patch });
      if (!resp.success) throw new Error(resp.error ?? 'save brief failed');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const renderModels = (
    <Collapse in={modelsExpanded}>
      <Stack spacing={1.25} sx={{ p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
        <AvailableModelSelector
          projectId={projectId}
          value={modelBrief}
          onChange={setModelBrief}
          label="Brief model"
          testId="research-study-model-brief"
        />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={searchProvider}
              label="Provider"
              onChange={(e) => setSearchProvider(e.target.value as ResearchSearchProvider)}
              data-testid="research-study-search-provider"
            >
              {availableSearchProviderOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Depth</InputLabel>
            <Select
              value={researchDepth}
              label="Depth"
              onChange={(e) => setResearchDepth(e.target.value as ResearchDepth)}
              data-testid="research-study-depth"
            >
              {RESEARCH_DEPTH_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
          {searchProvider !== 'auto' && (
            <AvailableModelSelector
              projectId={projectId}
              value={modelResearch}
              onChange={setModelResearch}
              label="Research model"
              providers={[searchProviderToAIProvider(searchProvider)]}
              modelUsage={researchDepth === 'deep' ? 'deep-research' : 'general'}
              size="small"
              sx={{ minWidth: 220, flex: 1 }}
              testId="research-study-model-research"
            />
          )}
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Effort</InputLabel>
            <Select
              value={reasoningEffort}
              label="Effort"
              onChange={(e) => setReasoningEffort(e.target.value as ResearchReasoningEffort)}
              data-testid="research-study-reasoning-effort"
            >
              {RESEARCH_EFFORT_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        <AvailableModelSelector
          projectId={projectId}
          value={modelAnalysis}
          onChange={setModelAnalysis}
          label="Analysis model"
          testId="research-study-model-analysis"
        />
      </Stack>
    </Collapse>
  );

  const phaseHeader = (phase: PhaseKey, label: string) => {
    const status = phaseStatus(run, phase);
    const isRunning = status === 'running';
    const taskId = run?.phases?.[phase]?.taskId ?? null;
    const phaseBadgeLabel =
      phase === 'brief'
        ? 'Composing brief'
        : phase === 'research'
          ? 'Deep research'
          : 'Analysing research';
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>{label}</Typography>
        {isRunning && (
          <CellTaskBadge
            taskId={taskId}
            label={phaseBadgeLabel}
            fallbackActive
            data-testid={`research-study-${phase}-task-badge`}
          />
        )}
        <Chip size="small" variant="outlined" label={status} />
      </Stack>
    );
  };

  const briefRunning = busy === 'brief' || phaseStatus(run, 'brief') === 'running';
  const researchRunning = busy === 'research' || phaseStatus(run, 'research') === 'running';
  const analysisRunning = busy === 'analysis' || phaseStatus(run, 'analysis') === 'running';
  const anyPhaseRunning =
    busy !== null
    || briefRunning
    || researchRunning
    || analysisRunning
    || phaseStatus(run, nextIncompletePhase(run)) === 'running';

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={run ? (run.title ?? 'Research study') : 'Add research study'}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: { xs: 'calc(100dvh - 32px)', md: '85vh' },
          maxHeight: { xs: 'calc(100dvh - 32px)', md: '85vh' },
        },
      }}
      data-testid="research-study-dialog"
    >
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}

        {!run && (
          <Stack spacing={1.25} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <TextField
              size="small"
              label="Study title"
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              placeholder={formatFocusTitle(topic, focusKind, aspectKey, customGoal)}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Focus</InputLabel>
                <Select value={focusKind} label="Focus" onChange={(e) => setFocusKind(e.target.value as FocusKind)}>
                  <MenuItem value="topic">Topic-wide</MenuItem>
                  <MenuItem value="aspect" disabled={aspects.length === 0}>Existing aspect</MenuItem>
                  <MenuItem value="custom">Custom goal</MenuItem>
                </Select>
              </FormControl>
              {focusKind === 'aspect' && (
                <FormControl size="small" sx={{ minWidth: 260, flex: 1 }}>
                  <InputLabel>Aspect</InputLabel>
                  <Select value={aspectKey} label="Aspect" onChange={(e) => setAspectKey(e.target.value)}>
                    {aspects.map((aspect) => (
                      <MenuItem key={aspect.aspectKey} value={aspect.aspectKey}>{aspect.aspectLabel}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(focusKind === 'custom' || focusKind === 'aspect') && (
                <TextField
                  size="small"
                  label={focusKind === 'custom' ? 'Research goal' : 'Optional angle override'}
                  value={customGoal}
                  onChange={(e) => setCustomGoal(e.target.value)}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
          </Stack>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="text"
            onClick={() => setModelsExpanded((v) => !v)}
            endIcon={<ExpandMoreIcon sx={{ transform: modelsExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }} />}
            data-testid="research-study-models-toggle"
          >
            Models
          </Button>
          <Box sx={{ flex: 1 }} />
          {!run && (
            <Button
              size="small"
              variant="outlined"
              startIcon={busy === 'run_all' ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
              onClick={runAll}
              disabled={Boolean(busy)}
              data-testid="research-study-run-all"
            >
              {busy === 'run_all' ? 'Starting...' : 'Run all phases'}
            </Button>
          )}
        </Stack>
        {renderModels}

        <Accordion expanded={expanded === 'brief'} onChange={(_, isExpanded) => setExpanded(isExpanded ? 'brief' : false)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{phaseHeader('brief', 'Phase 1: Compose brief')}</AccordionSummary>
          <AccordionDetails>
            {!run?.brief ? (
              <ResearchBriefInputForm
                projectId={projectId}
                topic={topic}
                themeNamesById={themeNamesById}
                projectPreview={projectPreview}
                busy={briefRunning}
                briefExists={false}
                onCompose={(payload) => composeBrief(payload, run?.id)}
              />
            ) : (
              <Stack spacing={1}>
                <ResearchBriefEditor brief={run.brief} saving={false} onSave={saveBrief} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={briefRunning ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                  disabled={Boolean(busy) || briefRunning}
                  onClick={() => composeBrief({ selectedPaths: [], hydratedData: undefined }, run.id)}
                >
                  {briefRunning ? 'Recomposing...' : 'Rerun Phase 1'}
                </Button>
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'research'} onChange={(_, isExpanded) => setExpanded(isExpanded ? 'research' : false)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{phaseHeader('research', 'Phase 2: Deep research')}</AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={!run?.brief || Boolean(busy) || researchRunning}
                startIcon={researchRunning ? <CircularProgress size={14} /> : null}
                onClick={runDeepResearch}
              >
                {researchRunning ? 'Researching...' : run?.research ? 'Rerun Phase 2' : 'Run Phase 2'}
              </Button>
              {run?.research && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {(run.research?.chunks ?? []).length} citations · {String(run.research?.aiSummary ?? '').length.toLocaleString()} chars
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined" startIcon={<OpenInFullIcon />} onClick={() => setFullTextOpen(true)}>
                      View full text
                    </Button>
                  </Stack>
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: 260, overflow: 'auto', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    {String(run.research?.aiSummary ?? '').slice(0, 2500)}
                    {String(run.research?.aiSummary ?? '').length > 2500 ? '\n\n...' : ''}
                  </Typography>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'analysis'} onChange={(_, isExpanded) => setExpanded(isExpanded ? 'analysis' : false)}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{phaseHeader('analysis', 'Phase 3: Analyse research')}</AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <Button
                size="small"
                variant="contained"
                disabled={!run?.research || Boolean(busy) || analysisRunning}
                startIcon={analysisRunning ? <CircularProgress size={14} /> : null}
                onClick={runAnalysis}
              >
                {analysisRunning ? 'Analysing...' : run?.analysis ? 'Rerun Phase 3' : 'Run Phase 3'}
              </Button>
              {run?.analysis && (
                <ResearchAnalysisCard
                  analysis={run.analysis}
                  projectId={projectId}
                  runId={run.id}
                  userId={userId}
                />
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <SDialog
          open={fullTextOpen}
          onClose={() => setFullTextOpen(false)}
          title="Deep research full text"
          maxWidth="md"
          fullWidth
        >
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
            {String(run?.research?.aiSummary ?? '')}
          </Typography>
        </SDialog>

        {anyPhaseRunning && (
          <Typography variant="caption" color="text.secondary">
            Phase output updates live when the background task completes.
          </Typography>
        )}
      </Stack>
    </SDialog>
  );
};

export default ResearchStudyDialog;
